import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { NgxSpinnerModule, NgxSpinnerService } from "ngx-spinner";
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';

// Translate
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

// Models.
import { ClientDivision } from '../../models/clientDivision';
import { VW_RelativePerson } from '../../models/RES/vwRelativePerson';
import { VW_PersonalReferenceByPerson } from '../../models/RES/vwPersonalReferenceByPerson';
import { EmploymentHistoryByPerson } from '../../models/RES/employmentHistoryByPerson';
import { GenericObjectViewModel } from '../../models/RES/genericObjectViewModel';
import { VW_State } from '../../models/RES/vwState';
import { VW_Locality } from '../../models/RES/vwLocality';
import { VW_SocialNumberType } from '../../models/RES/vwSocialNumberType';
import { VW_TaxNumberType } from '../../models/RES/vwTaxNumberType';
import { VW_IdentityNumberType } from '../../models/RES/vwIdentityNumberType';

// Components.
import { AddJobDialogComponent } from './add-job-dialog/add-job-dialog.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { AddPersonalReferenceDialogComponent } from './add-personal-reference-dialog/add-personal-reference-dialog.component';
import { AddRelativeDialogComponent } from './add-relative-dialog/add-relative-dialog.component';

// Services.
import { ClientDivisionsService } from '../../services/clientDivisions.service';
import { EmploymentHistoryByPersonsService } from '../../services/employmentHistoryByPersons.Service';
import { RelativePeopleService } from '../../services/relativePeople.service';
import { SIESPServicesService } from '../../services/siespServices.service';
import { PersonalReferencesByPersonService } from '../../services/personalReferencesByPerson.service';
import { GendersService } from '../../services/genders.service';
import { StatesService } from '../../services/states.service';
import { LocalitiesService } from '../../services/localities.service';
import { SocialNumberTypesService } from '../../services/socialNumberTypes.service';
import { CountriesService } from '../../services/countries.service';
import { OccupationsService } from '../../services/occupations.service';
import { MaritalStatusesService } from '../../services/maritalStatuses.service';
import { TaxNumberTypesService } from '../../services/taxNumberTypes.service';
import { IdentityNumberTypesService } from '../../services/identityNumberTypes.service';
import { SchoolingService } from '../../services/schooling.service';

// Utility.
import { CustomDateAdapter, MY_DATE_FORMATS } from '../../common/utility/CustomDateAdapter';

// Material
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { DateAdapter, ErrorStateMatcher, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: any, form: any): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@Component({
  selector: 'app-create-request',
  templateUrl: './create-request.component.html',
  styleUrls: ['./create-request.component.scss'],
  standalone: true,
  imports: [
    MatTabsModule,
    MatIconModule,
    TranslatePipe,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDividerModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    CommonModule,
    MatExpansionModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    FormsModule,
    MatDatepickerModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    NgxSpinnerModule,
    MatTooltipModule,
    MatAutocompleteModule,
    MatNativeDateModule,
    NgxMatSelectSearchModule
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
  ],
})
export class CreateRequestComponent implements OnInit {
  constructor(
    private dialog: MatDialog,
    private fb: FormBuilder,
    public translate: TranslateService,
    private route: ActivatedRoute,
    private apiS: SIESPServicesService,
    private apiCD: ClientDivisionsService,
    private apiRP: RelativePeopleService,
    private apiEH: EmploymentHistoryByPersonsService,
    private apiPRP: PersonalReferencesByPersonService,
    private apiG: GendersService,
    private apiINT: IdentityNumberTypesService,
    private apiTNT: TaxNumberTypesService,
    private apiSNT: SocialNumberTypesService,
    private apiC: CountriesService,
    private apiO: OccupationsService,
    private apiMS: MaritalStatusesService,
    private apiST: StatesService,
    private apiLT: LocalitiesService,
    private apiSC: SchoolingService,
    private spinner: NgxSpinnerService,
  ) {}
  router = inject(Router);
  private _snackBar = inject(MatSnackBar);

  isLoading: boolean = false;
  loadingPercentage: number = 0;

  ServiceId: number = 1;
  Service: any;

  matcher = new MyErrorStateMatcher();

  mainForm: FormGroup = this.fb.group({
    id: [this.ServiceId],
    division_Id: [{value: 0, disabled: true}],
    person_Id: [0],
    name: ['', [Validators.minLength(3)]],
    firstLastName: [''],
    secondLastName: [''],
    gender_Id: [[null, Validators.required]],
    maritalStatus_Id: [[null, Validators.required]],
    schooling_Id: [[null]],
    profession: [''],
    identityType_Id: [[null, Validators.required]],
    nationalId: [['', Validators.required]],
    taxType_Id: [null],
    taxId: [''],
    socialType_Id: [null],
    socialId: [''],
    address: this.fb.group({
      country_Id: [null],
      state_Id: [[null, Validators.required]],
      city_Id: [[null, Validators.required]],
      street: [['', Validators.required]],
      intNumber: [''],
      extNumber: [['', Validators.required]],
      sublocality: [['', Validators.required]],
      postalCode: [null]
    }),
    birthData: this.fb.group({
      birthCountry_Id: [[null, Validators.required]],
      birthState_Id: [[null, Validators.required]],
      birthCity_Id: [[null, Validators.required]],
      birthDate: [[null, Validators.required]]
    }),
    notes: [''],
    father: this.fb.group({
      id: [0],
      isUnknown: [false],
      name: [''],
      firstLastName: [''],
      secondLastName: [''],
      birthDate: [null],
      occupation_Id: [null],
      country_Id: [null],
      state_Id: [null]
    }),
    mother: this.fb.group({
      id: [0],
      isUnknown: [false],
      name: [''],
      firstLastName: [''],
      secondLastName: [''],
      birthDate: [null],
      occupation_Id: [null],
      country_Id: [null],
      state_Id: [null]
    }),
    spouse: this.fb.group({
      id: [0],
      name: [''],
      firstLastName: [''],
      secondLastName: [''],
      birthDate: [null],
      occupation_Id: [null],
      country_Id: [null],
      state_Id: [null]
    })
  });

  clientDivisions: ClientDivision[] = [];
  genders: GenericObjectViewModel[] = [];
  maritalStatues: GenericObjectViewModel[] = [];
  iNT: VW_IdentityNumberType[] = [];
  tNT: VW_TaxNumberType[] = [];
  sNT: VW_SocialNumberType[] = [];
  countries: GenericObjectViewModel[] = [];
  states: VW_State[] = [];
  localities: VW_Locality[] = [];
  birthCountries: GenericObjectViewModel[] = [];
  birthStates: VW_State[] = [];
  birthLocalities: VW_Locality[] = [];
  occupations: GenericObjectViewModel[] = [];
  schoolings: GenericObjectViewModel[] = [];

  // Relatives states.
  // Father.
  fCountries: GenericObjectViewModel[] = [];
  fStates: VW_State[] = [];

  // Mother.
  mCountries: GenericObjectViewModel[] = [];
  mStates: VW_State[] = [];

  // Spouse.
  sCountries: GenericObjectViewModel[] = [];
  sStates: VW_State[] = [];

  ngOnInit(): void {
    this.mainForm.get('taxType_Id')?.valueChanges.subscribe(value => {
      const taxId = this.mainForm.get('taxId');

      if (value) {
        taxId?.setValidators([Validators.required]);
      } else {
        taxId?.clearValidators();
      }

      taxId?.updateValueAndValidity({ emitEvent: false });
    });

    this.mainForm.get('taxId')?.valueChanges.subscribe(value => {
      const taxType = this.mainForm.get('taxType_Id');

      if (value) {
        taxType?.setValidators([Validators.required]);
      } else {
        taxType?.clearValidators();
      }

      taxType?.updateValueAndValidity({ emitEvent: false });
    });


    this.mainForm.get('socialType_Id')?.valueChanges.subscribe(value => {
      const socialId = this.mainForm.get('socialId');

      if (value) {
        socialId?.setValidators([Validators.required]);
      } else {
        socialId?.clearValidators();
      }

      socialId?.updateValueAndValidity({ emitEvent: false });
    });

    this.mainForm.get('socialId')?.valueChanges.subscribe(value => {
      const socialType = this.mainForm.get('socialType_Id');

      if (value) {
        socialType?.setValidators([Validators.required]);
      } else {
        socialType?.clearValidators();
      }

      socialType?.updateValueAndValidity({ emitEvent: false });
    });
    this.isLoading = true;
    this.route.queryParams.subscribe((params) => {
      this.ServiceId = params['ServiceId'];
      if(this.ServiceId){
        this.mainForm.get('father.isUnknown')?.valueChanges.subscribe((isUnknown: boolean) => {
          this.toggleParentFields('father', isUnknown);
        });
      
        this.mainForm.get('mother.isUnknown')?.valueChanges.subscribe((isUnknown: boolean) => {
          this.toggleParentFields('mother', isUnknown);
        });

        this.mainForm.get('identityType_Id')?.valueChanges.subscribe((identityTypeId: number) => {
          this.handleIdentityTypeChange(identityTypeId);
        });
      
        this.mainForm.get('nationalId')?.valueChanges.subscribe((curp: string) => {
          this.updateGenderFromCURP(curp);
        });
      }else{
        this.openSnackBar("There is not selected service.");
        this.router.navigate(['/dashboard']);
      }
    });

    this.loadFormData();
    this.loadEmploymentHistory();
    this.loadPersonalReferences();
  }

  highlightMissing() {
    this.mainForm.markAllAsTouched();
  }

  onlyDigits(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'
    ];
    if (allowedKeys.includes(event.key)) {
      return; // dejamos pasar control, flechas, etc.
    }
    // Si no es dígito (0–9), prevenimos
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  handleIdentityTypeChange(identityTypeId: number) {
    const curpId = 1;
  
    if (identityTypeId === curpId) {
      
      this.mainForm.get('gender_Id')?.setValue(null);
      this.mainForm.get('gender_Id')?.disable();
    } else {
      
      this.mainForm.get('gender_Id')?.enable();
    }
  }

  updateGenderFromCURP(curp: string) {
    const curpId = 1;
    const identityTypeId = this.mainForm.get('identityType_Id')?.value;
  
    if (identityTypeId === curpId && curp?.length >= 18) { 
      const genderChar = curp[10];
      let genderValue = 0;
  
      if (genderChar === 'H') {
        genderValue = this.getGenderIdByName('Masculino');
      } else if (genderChar === 'M') {
        genderValue = this.getGenderIdByName('Femenino');
      } else if (genderChar === 'X') {
        genderValue = this.getGenderIdByName('Otro');
      }
      
      this.mainForm.get('gender_Id')?.setValue(genderValue);
    }
  }   
  
  getGenderIdByName(name: string): number {
    const gender = this.genders.find(g => g.name.toLowerCase() === name.toLowerCase());
    return gender ? gender.id : 0;
  }  

  toggleParentFields(parentKey: string, isUnknown: boolean) {
    const parentGroup = this.mainForm.get(parentKey) as FormGroup;
  
    if (isUnknown) {
      parentGroup.patchValue({
        name: '',
        firstLastName: '',
        secondLastName: '',
        birthDate: null,
        occupation_Id: 0,
        country_Id: 0,
        state_Id: 0
      }, { emitEvent: false });
  
      Object.keys(parentGroup.controls).forEach(key => {
        if (key !== 'isUnknown') {
          parentGroup.get(key)?.disable({ emitEvent: false });
        }
      });
    } else {
      Object.keys(parentGroup.controls).forEach(key => {
        parentGroup.get(key)?.enable({ emitEvent: false });
      });
    }
  }  

  private loadFormData(){
    this.apiCD.GetClientDivisionsByCognitoUser().subscribe(response => {
      if (response.success === 1) {
        this.clientDivisions = response.data;
        this.loadingPercentage = 10;
        this.apiG.GetAll().subscribe({
          next: (response) => {
            if (response.success === 1) {
              this.genders = response.data;
              this.loadingPercentage = 20;
              this.apiINT.GetAll().subscribe({
                next: (response) => {
                  if (response.success === 1) {
                    this.iNT = response.data;
                    this.loadingPercentage = 30;
                    this.apiTNT.GetAll().subscribe({
                      next: (response) => {
                        if (response.success === 1) {
                          this.tNT = response.data;
                          this.loadingPercentage = 40;
                          this.apiSNT.GetAll().subscribe({
                            next: (response) => {
                              if (response.success === 1) {
                                this.sNT = response.data;
                                this.loadingPercentage = 50;
                                this.apiC.GetAllCountries().subscribe({
                                  next: (response) => {
                                    if (response.success === 1) {
                                      this.countries = response.data;
                                      this.birthCountries = response.data;
                                      this.fCountries = response.data;
                                      this.mCountries = response.data;
                                      this.sCountries = response.data;
                                      this.loadingPercentage = 60;
                                      this.apiO.GetAll().subscribe({
                                        next: (response) => {
                                          if (response.success === 1) {
                                            this.occupations = response.data;
                                            this.loadingPercentage = 70;
                                            this.apiMS.GetAll().subscribe({
                                              next: (response) => {
                                                if (response.success === 1) {
                                                  this.maritalStatues = response.data;
                                                  this.loadingPercentage = 75;
                                                  this.apiSC.GetAll().subscribe({
                                                    next: (response) => {
                                                      if (response.success === 1) {
                                                        this.schoolings = response.data;
                                                        this.loadingPercentage = 80;
                                                        this.loadService(this.ServiceId); // (*￣3￣)╭
                                                      } else {
                                                        this.openSnackBar(response.message);
                                                      }
                                                    },
                                                    error: (error) => {
                                                      const statusCode = error.status;
                                                      const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
                                                
                                                      this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
                                                      console.error(`Error ${statusCode}`, error);
                                                    },
                                                  });
                                                } else {
                                                  this.openSnackBar(response.message);
                                                }
                                              },
                                              error: (error) => {
                                                const statusCode = error.status;
                                                const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
                                          
                                                this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
                                                console.error(`Error ${statusCode}`, error);
                                              },
                                            });
                                          } else {
                                            this.openSnackBar(response.message);
                                          }
                                        },
                                        error: (error) => {
                                          const statusCode = error.status;
                                          const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
                                    
                                          this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
                                          console.error(`Error ${statusCode}`, error);
                                        },
                                      });
                                    } else {
                                      this.openSnackBar(response.message);
                                    }
                                  },
                                  error: (error) => {
                                    const statusCode = error.status;
                                    const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
                              
                                    this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
                                    console.error(`Error ${statusCode}`, error);
                                  },
                                });
                              } else {
                                this.openSnackBar(response.message);
                              }
                            },
                            error: (error) => {
                              const statusCode = error.status;
                              const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
                        
                              this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
                              console.error(`Error ${statusCode}`, error);
                            },
                          });
                        } else {
                          this.openSnackBar(response.message);
                        }
                      },
                      error: (error) => {
                        const statusCode = error.status;
                        const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
                  
                        this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
                        console.error(`Error ${statusCode}`, error);
                      },
                    });
                  } else {
                    this.openSnackBar(response.message);
                  }
                },
                error: (error) => {
                  const statusCode = error.status;
                  const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
            
                  this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
                  console.error(`Error ${statusCode}`, error);
                },
              });
            } else {
              this.openSnackBar(response.message);
            }
          },
          error: (error) => {
            const statusCode = error.status;
            const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
      
            this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
            console.error(`Error ${statusCode}`, error);
          },
        });
      } else {
        this.openSnackBar(response.message);
      }
    }, error => {
      this.openSnackBar("An error occured trying to connect to web services.");
    });
  }

  // Load service info.
  private loadService(dataId: number) {
    this.apiS.GetSIESPService(dataId).subscribe({
      next: (response) => {
        if (response.success === 1) {
          this.Service = response.data;
          const serviceData = response.data;
  
          this.mainForm.patchValue({
            id: this.ServiceId,
            division_Id: serviceData.division_Id,
            person_Id: this.Service.person_Id,
            name: serviceData.name || '',
            firstLastName: serviceData.firstLastName || '',
            secondLastName: serviceData.secondLastName || '',
            gender_Id: serviceData.gender_Id || null,
            maritalStatus_Id: serviceData.maritalStatus_Id || null,
            schooling_Id: serviceData.schooling_Id || null,
            profession: serviceData.profession || '',
            identityType_Id: serviceData.identityType_Id || null,
            nationalId: serviceData.nationalId || '',
            taxType_Id: serviceData.taxType_Id || null,
            taxId: serviceData.taxId || '',
            socialType_Id: serviceData.socialType_Id || null,
            socialId: serviceData.socialId || '',
            address: {
              country_Id: serviceData.address?.country_Id || null,
              state_Id: serviceData.address?.state_Id || null,
              city_Id: serviceData.address?.city_Id || null,
              street: serviceData.address?.street || '',
              intNumber: serviceData.address?.intNumber || '',
              extNumber: serviceData.address?.extNumber || '',
              sublocality: serviceData.address?.sublocality || '',
              postalCode: serviceData.address?.postalCode || null
            },
            birthData: {
              birthCountry_Id: serviceData.birthData?.birthCountry_Id || null,
              birthState_Id: serviceData.birthData?.birthState_Id || null,
              birthCity_Id: serviceData.birthData?.birthCity_Id || null,
              birthDate: serviceData.birthData?.birthDate || null
            },
            notes: serviceData.notes || '',
            father: {
              id: serviceData.father?.id || 0,
              isUnknown: serviceData.father?.isUnknown || false,
              name: serviceData.father?.name || '',
              firstLastName: serviceData.father?.firstLastName || '',
              secondLastName: serviceData.father?.secondLastName || '',
              birthDate: serviceData.father?.birthDate || null,
              occupation_Id: serviceData.father?.occupation_Id || null,
              country_Id: serviceData.father?.country_Id || null,
              state_Id: serviceData.father?.state_Id || null
            },
            mother: {
              id: serviceData.mother?.id || null,
              isUnknown: serviceData.mother?.isUnknown || false,
              name: serviceData.mother?.name || '',
              firstLastName: serviceData.mother?.firstLastName || '',
              secondLastName: serviceData.mother?.secondLastName || '',
              birthDate: serviceData.mother?.birthDate || null,
              occupation_Id: serviceData.mother?.occupation_Id || null,
              country_Id: serviceData.mother?.country_Id || null,
              state_Id: serviceData.mother?.state_Id || null
            },
            spouse: {
              id: serviceData.spouse?.id || null,
              name: serviceData.spouse?.name || '',
              firstLastName: serviceData.spouse?.firstLastName || '',
              secondLastName: serviceData.spouse?.secondLastName || '',
              birthDate: serviceData.spouse?.birthDate || null,
              occupation_Id: serviceData.spouse?.occupation_Id || null,
              country_Id: serviceData.spouse?.country_Id || null,
              state_Id: serviceData.spouse?.state_Id || null
            }
          });

          // Address selectors.
          if(this.Service.address.country_Id != null){
            this.onCountryChange(this.Service.address.country_Id);
            if(this.Service.address.state_Id != null){
              this.onStateChange(this.Service.address.state_Id);
            }
          }

          // Birth data selectors.
          if(this.Service.birthData.birthCountry_Id != null){
            this.onBirthCountryChange(this.Service.birthData.birthCountry_Id);
            if(this.Service.birthData.birthState_Id != null){
              this.onBirthStateChange(this.Service.birthData.birthState_Id);
            }
          }

          // Spouse data selectors.
          if(this.Service.spouse?.country_Id != null){
            this.onsCountryChange(this.Service.spouse.country_Id);
          }

          // Father data selectors.
          if(this.Service.father?.country_Id != null){
            this.onfCountryChange(this.Service.father.country_Id);
          }

          // Mother data selectors.
          if(this.Service.mother?.country_Id != null){
            this.onmCountryChange(this.Service.mother.country_Id);
          }

          if ((this.Service.status_Id === 3 || (this.Service.status_Id === 4  && this.Service.scoreRisk != 40))) {
            this.mainForm.disable();
            this.mainForm.get('father.isUnknown')?.disable({ onlySelf: true, emitEvent: false });
            this.mainForm.get('mother.isUnknown')?.disable({ onlySelf: true, emitEvent: false });
            setTimeout(() => {
              const identityType = this.mainForm.get('identityType_Id')?.value;

              if (identityType !== 1 && this.mainForm.get('gender_Id')) {
                this.mainForm.get('gender_Id')!.disable({ onlySelf: true, emitEvent: false });
              }
            }, 100);
          }

          this.loadingPercentage = 100;
          this.isLoading = false; // (*￣3￣)╭
          this.loadRelatives();
        } else {
          this.openSnackBar(response.message);
        }
      },
      error: (error) => {
        const statusCode = error.status;
        const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
  
        this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
        console.error(`Error ${statusCode}`, error);
      }
    });
  }

  onSubmit(): void {
    const taxType = this.mainForm.get('taxType_Id')?.value;
    const taxId = this.mainForm.get('taxId')?.value;

    const socialType = this.mainForm.get('socialType_Id')?.value;
    const socialId = this.mainForm.get('socialId')?.value;

    if ((taxType && !taxId) || (!taxType && taxId)) {
      this.openSnackBar('TAX ID: Required fields');
      return;
    }

    if ((socialType && !socialId) || (!socialType && socialId)) {
      this.openSnackBar('SOCIAL ID: Required fields');
      return;
    }

    this.spinner.show();
    this.apiS.Save(this.mainForm.getRawValue()).subscribe({
      next: (response) => {
        if (response.success === 1) {
          this.spinner.hide();
          this.openSnackBar("Success!");
          this.Service.requestProgress = response.data;
          if(!this.Service.updateOverResult && this.Service.scoreRisk === 40){
            this.Service.updateOverResult = true;
          }
        } else {
          this.openSnackBar(response.message);
        }
      },
      error: (error) => {
        this.spinner.hide();
        const statusCode = error.status;
        const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
  
        this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
        console.error(`Error ${statusCode}`, error);
      },
    });
  }

  sendToSIES(){
    this.spinner.show();
    if (this.Service.requestProgress === 100) {
      this.apiS.SendToSIES(this.ServiceId).subscribe({
        next: (response) => {
          if (response.success === 1) {
            this.spinner.hide();
            this.openSnackBar("Service sent!");
            this.router.navigate(['/dashboard']);
          } else {
            this.spinner.hide();
            this.openSnackBar(response.message);
          }
        },
        error: (error) => {
          this.spinner.hide();
          const statusCode = error.status;
          const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
    
          this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
          console.error(`Error ${statusCode}`, error);
        },
      });
    } else {
      this.spinner.hide();
      this.openSnackBar('Invalid form to send');
    }
  }

  resendToSIES(){
    this.spinner.show();
    if (this.Service.requestProgress === 100 && this.Service.status_Id === 4 && this.Service.scoreRisk === 40 && this.Service.updateOverResult) {
      this.apiS.ResendToSIES(this.ServiceId).subscribe({
        next: (response) => {
          if (response.success === 1) {
            this.spinner.hide();
            this.openSnackBar("Service sent!");
            this.router.navigate(['/dashboard']);
          } else {
            this.openSnackBar(response.message);
          }
        },
        error: (error) => {
          this.spinner.hide();
          const statusCode = error.status;
          const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
    
          this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
          console.error(`Error ${statusCode}`, error);
        },
      });
    } else {
      this.spinner.hide();
      console.error('Invalid form to send');
    }
  }

  backToDashboard(){
    if (((this.Service.status_Id === 1 || this.Service.status_Id === 2) && !(this.Service.status_Id === 4  && this.Service.scoreRisk === 40))) {
      this.onSubmit(); 
    }
    this.router.navigate(['/dashboard']);
  }

  // Selectors.
  getSelectValue(event: Event): number {
    return Number((event.target as HTMLSelectElement).value);
  }

  // Address selectors.
  onCountryChange(countryId: number): void {
    this.states = [];
    this.localities = [];
    this.apiST.GetStatesByCountry(countryId).subscribe({
      next: (response) => {
        if (response.success === 1) {
          this.states = response.data;
        }else{
          this.openSnackBar(response.message);
        }
      },
      error: (error) => {
        const statusCode = error.status;
        const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
  
        this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
        console.error(`Error ${statusCode}`, error);
      },
    });
  }

  onStateChange(stateId: number): void {
    
    this.localities = [];
    this.apiLT.GetLocalitiesByState(stateId).subscribe({
      next: (response) => {
        if (response.success === 1) {
          this.localities = response.data;
        }else{
          this.openSnackBar(response.message);
        }
      },
      error: (error) => {
        const statusCode = error.status;
        const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
  
        this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
        console.error(`Error ${statusCode}`, error);
      },
    });
  }

  // Birth data selectors.
  onBirthCountryChange(countryId: number): void {
    
    this.birthStates = [];
    this.birthLocalities = [];
    this.apiST.GetStatesByCountry(countryId).subscribe({
      next: (response) => {
        if (response.success === 1) {
          this.birthStates = response.data;
        }else{
          this.openSnackBar(response.message);
        }
      },
      error: (error) => {
        const statusCode = error.status;
        const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
  
        this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
        console.error(`Error ${statusCode}`, error);
      },
    });
  }

  onBirthStateChange(stateId: number): void {
    
    this.birthLocalities = [];
    this.apiLT.GetLocalitiesByState(stateId).subscribe({
      next: (response) => {
        if (response.success === 1) {
          this.birthLocalities = response.data;
        }else{
          this.openSnackBar(response.message);
        }
      },
      error: (error) => {
        const statusCode = error.status;
        const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
  
        this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
        console.error(`Error ${statusCode}`, error);
      },
    });
  }

  // Father selector.
  onfCountryChange(countryId: number): void {

    this.fStates = [];
    this.apiST.GetStatesByCountry(countryId).subscribe({
      next: (response) => {
        if (response.success === 1) {
          this.fStates = response.data;
        }else{
          this.openSnackBar(response.message);
        }
      },
      error: (error) => {
        const statusCode = error.status;
        const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
  
        this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
        console.error(`Error ${statusCode}`, error);
      },
    });
  }

  // Mother selector.
  onmCountryChange(countryId: number): void {
    
    this.mStates = [];
    this.apiST.GetStatesByCountry(countryId).subscribe({
      next: (response) => {
        if (response.success === 1) {
          this.mStates = response.data;
        }else{
          this.openSnackBar(response.message);
        }
      },
      error: (error) => {
        const statusCode = error.status;
        const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
  
        this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
        console.error(`Error ${statusCode}`, error);
      },
    });
  }

  // Spouse selector.
  onsCountryChange(countryId: number): void {
    
    this.sStates = [];
    this.apiST.GetStatesByCountry(countryId).subscribe({
      next: (response) => {
        if (response.success === 1) {
          this.sStates = response.data;
        }else{
          this.openSnackBar(response.message);
        }
      },
      error: (error) => {
        const statusCode = error.status;
        const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
  
        this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
        console.error(`Error ${statusCode}`, error);
      },
    });
  }
  //

  // Relative People starts.
  private loadRelatives(){
    this.apiRP.GetOtherRelativesByPerson(this.ServiceId).subscribe({
      next: (response) => {
        if (response.success === 1) {
          this.relativePeopleData = response.data;

          this.relativesDataSource.data = this.relativePeopleData;
          this.relativesDataSource.paginator = this.relativesPaginator;
          this.relativesDataSource.sort = this.relativesSort;
        } else {
          this.openSnackBar(response.message);
        }
      },
      error: (error) => {
        const statusCode = error.status;
        const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
  
        this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
        console.error(`Error ${statusCode}`, error);
      },
    });
  }

  relativePeopleData: VW_RelativePerson[] = [];
  relativesDisplayedColumns: string[] = ['relative', 'firstName', 'firstLastName', 'secondLastName', 'birthDate', 'occupationName', 'stateName', 'actions'];
  
  relativesDataSource = new MatTableDataSource<any>([]);
  @ViewChild(MatPaginator) relativesPaginator!: MatPaginator;
  @ViewChild(MatSort) relativesSort!: MatSort;

  applyFilterRP(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.relativesDataSource.filter = filterValue.trim().toLowerCase();

    if (this.relativesDataSource.paginator) {
      this.relativesDataSource.paginator.firstPage();
    }
  }

  openAddRelativeDialog(){
    const dialogRef = this.dialog.open(AddRelativeDialogComponent, {
      width: '400px',
      data: {
        personId: this.Service.person_Id
      },
      disableClose: true,
    });
  
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.apiRP.Create(result).subscribe({
          next: (response) => {
            if (response.success === 1) {

              this.loadRelatives();
            } else {
              this.openSnackBar(response.message);
            }
          },
          error: (error) => {
            const statusCode = error.status;
            const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
      
            this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
            console.error(`Error ${statusCode}`, error);
          },
        });
      }
    });
  }

  editRelative(data: VW_RelativePerson){
    const dialogRef = this.dialog.open(AddRelativeDialogComponent, {
      width: '400px',
      data: data,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.apiRP.Edit(result).subscribe({
          next: (response) => {
            if (response.success === 1) {

              this.loadRelatives();
            } else {
              this.openSnackBar(response.message);
            }
          },
          error: (error) => {
            const statusCode = error.status;
            const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
      
            this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
            console.error(`Error ${statusCode}`, error);
          },
        });
      }
    });
  }

  confirmDeleteRelative(data: VW_RelativePerson){
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        message: `¿Estás seguro de que deseas eliminar a ${data.relativetypename} ${data.firstname} ${data.firstlastname} ${data.secondlastname}?`
      },
      disableClose: true
    });
  
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.apiRP.Disable(data.id).subscribe({
          next: (response) => {
            if (response.success === 1) {
              this.openSnackBar("Successful!");
              this.loadRelatives();
            } else {
              this.openSnackBar(response.message);
            }
          },
          error: (error) => {
            const statusCode = error.status;
            const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
      
            this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
            console.error(`Error ${statusCode}`, error);
          },
        });
      }
    });
  }
  // Relative People ends.

  // Employment History starts.
  private loadEmploymentHistory(){
    this.apiEH.GetEmploymentHistoryByPerson(this.ServiceId).subscribe({
      next: (response) => {
        if (response.success === 1) {
          this.employmentHistoryData = response.data;

          this.employmentHistoryDataSource.data = this.employmentHistoryData;
          this.employmentHistoryDataSource.paginator = this.employmentHistoryPaginator;
          this.employmentHistoryDataSource.sort = this.employmentHistorySort;
        } else {
          this.openSnackBar(response.message);
        }
      },
      error: (error) => {
        const statusCode = error.status;
        const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
  
        this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
        console.error(`Error ${statusCode}`, error);
      },
    });
  }

  employmentHistoryData: EmploymentHistoryByPerson[] = [];
  employmentHistoryDisplayedColumns: string[] = ['company', 'position', 'startDate', 'endDate', 'actions'];
  
  employmentHistoryDataSource = new MatTableDataSource<any>([]);
  @ViewChild(MatPaginator) employmentHistoryPaginator!: MatPaginator;
  @ViewChild(MatSort) employmentHistorySort!: MatSort;

  applyFilterEH(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.employmentHistoryDataSource.filter = filterValue.trim().toLowerCase();

    if (this.employmentHistoryDataSource.paginator) {
      this.employmentHistoryDataSource.paginator.firstPage();
    }
  }

  openAddEHDialog(): void {
    const dialogRef = this.dialog.open(AddJobDialogComponent, {
      width: '400px',
      data: {
        personId: this.Service.person_Id
      },
      disableClose: true,
    });
  
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.apiEH.Create(result).subscribe({
          next: (response) => {
            if (response.success === 1) {

              this.loadEmploymentHistory();
            } else {
              this.openSnackBar(response.message);
            }
          },
          error: (error) => {
            const statusCode = error.status;
            const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
      
            this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
            console.error(`Error ${statusCode}`, error);
          },
        });
      }
    });
  }

  editEH(data: EmploymentHistoryByPerson){
    const dialogRef = this.dialog.open(AddJobDialogComponent, {
      width: '400px',
      data: data,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.apiEH.Edit(result).subscribe({
          next: (response) => {
            if (response.success === 1) {

              this.loadEmploymentHistory();
            } else {
              this.openSnackBar(response.message);
            }
          },
          error: (error) => {
            const statusCode = error.status;
            const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
      
            this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
            console.error(`Error ${statusCode}`, error);
          },
        });
      }
    });
  }

  confirmDeleteEH(data: EmploymentHistoryByPerson){
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        message: `¿Estás seguro de que deseas eliminar el empleo ${data.position} en ${data.company}?`
      },
      disableClose: true
    });
  
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.apiEH.Disable(data.id).subscribe({
          next: (response) => {
            if (response.success === 1) {
              this.openSnackBar("Successful!");
              this.loadEmploymentHistory();
            } else {
              this.openSnackBar(response.message);
            }
          },
          error: (error) => {
            const statusCode = error.status;
            const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
      
            this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
            console.error(`Error ${statusCode}`, error);
          },
        });
      }
    });
  }
  // Employment History ends.

  // Personal references starts.
  private loadPersonalReferences(){
    this.apiPRP.GetReferencesByPerson(this.ServiceId).subscribe({
      next: (response) => {
        if (response.success === 1) {
          this.prpData = response.data;

          this.prpDataSource.data = this.prpData;
          this.prpDataSource.paginator = this.prpPaginator;
          this.prpDataSource.sort = this.prpSort;
        } else {
          this.openSnackBar(response.message);
        }
      },
      error: (error) => {
        const statusCode = error.status;
        const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
  
        this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
        console.error(`Error ${statusCode}`, error);
      },
    });
  }

  prpData: VW_PersonalReferenceByPerson[] = [];
  prpDisplayedColumns: string[] = ['firstName', 'firstLastName', 'secondLastName', 'age', 'occupation', 'statename', 'actions'];
  
  prpDataSource = new MatTableDataSource<any>([]);
  @ViewChild(MatPaginator) prpPaginator!: MatPaginator;
  @ViewChild(MatSort) prpSort!: MatSort;

  applyFilterprp(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.prpDataSource.filter = filterValue.trim().toLowerCase();

    if (this.prpDataSource.paginator) {
      this.prpDataSource.paginator.firstPage();
    }
  }

  openAddprpDialog(): void {
    const dialogRef = this.dialog.open(AddPersonalReferenceDialogComponent, {
      width: '400px',
      data: {
        personId: this.Service.person_Id
      },
      disableClose: true,
    });
  
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.apiPRP.Create(result).subscribe({
          next: (response) => {
            if (response.success === 1) {

              this.loadPersonalReferences();
            } else {
              this.openSnackBar(response.message);
            }
          },
          error: (error) => {
            const statusCode = error.status;
            const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
      
            this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
            console.error(`Error ${statusCode}`, error);
          },
        });
      }
    });
  }

  editprp(data: VW_PersonalReferenceByPerson){
    const dialogRef = this.dialog.open(AddPersonalReferenceDialogComponent, {
      width: '400px',
      data: data,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.apiPRP.Edit(result).subscribe({
          next: (response) => {
            if (response.success === 1) {

              this.loadPersonalReferences();
            } else {
              this.openSnackBar(response.message);
            }
          },
          error: (error) => {
            const statusCode = error.status;
            const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
      
            this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
            console.error(`Error ${statusCode}`, error);
          },
        });
      }
    });
  }

  confirmDeleteprp(data: VW_PersonalReferenceByPerson){
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        message: `¿Estás seguro de que deseas eliminar a ${data.firstname} ${data.firstlastname} ${data.secondlastname} de las referencias?`
      },
      disableClose: true
    });
  
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.apiPRP.Disable(data.id).subscribe({
          next: (response) => {
            if (response.success === 1) {
              this.openSnackBar("Successful!");
              this.loadPersonalReferences();
            } else {
              this.openSnackBar(response.message);
            }
          },
          error: (error) => {
            const statusCode = error.status;
            const errorMessage = error.error?.message || "An error occurred trying to connect to web services.";
      
            this.openSnackBar(`Error ${statusCode}: ${errorMessage}`);
            console.error(`Error ${statusCode}`, error);
          },
        });
      }
    });
  }
  // Personal references ends.

  openSnackBar(Message: string) {
    this._snackBar.open(Message, "Dismiss", { duration: 5000 });
  }
}